import { useEffect, useState } from 'react'
import { apiGet, apiPost } from '../api/client'

type ProviderState = {
  current: string
  available: string[]
  large_model?: string | null
  small_model?: string | null
}

// Same key SiteGate / TestingModeGate / TestingPasswordPanel use.
// Inline-duplicated rather than imported because all of these go away
// at public launch and a shared module would outlive its callers.
const SITE_PASSWORD_KEY = 'siteAccess'

/**
 * Dev-only floating dropdown to swap the backend's LLM provider at runtime.
 *
 * Surfaces:
 *   • The active provider (selectable)
 *   • The resolved large + small model strings — these are what the next
 *     llm.chat() call will actually pass for large-tier phases
 *     (knowledge_map, assessment_setup) and small-tier phases
 *     (assessment, tutoring, summary). Sourced from the new
 *     `/api/dev/provider` response fields; safe to render absent for
 *     older backends (the optional chain renders the legacy widget).
 *
 * Only renders when:
 *   - Vite is in dev mode (import.meta.env.DEV), OR
 *   - VITE_SHOW_DEV_TOOLS=1 is set at build time
 * AND the backend has DEV_TOOLS=1 (otherwise the /api/dev/provider endpoint
 * returns 404 and we hide the widget).
 *
 * Defers the initial fetch until the site-password gate has cleared
 * (gh#247). On staging, this widget mounts via App.tsx as a sibling
 * of the routes — including on public routes that bypass SiteGate
 * without unlock — so a fresh visitor on `/` would otherwise produce
 * a 401 on `/api/dev/provider` before they had entered the password.
 * We wait for either an existing localStorage entry or a
 * `sitegate:unlocked` window event before firing.
 */
export default function ProviderSwitcher() {
  const devEnabled =
    import.meta.env.DEV ||
    import.meta.env.VITE_SHOW_DEV_TOOLS === '1'

  const [state, setState] = useState<ProviderState | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!devEnabled) return

    let cancelled = false
    const fetchProvider = () => {
      apiGet<ProviderState>('/dev/provider')
        .then((s) => {
          if (!cancelled) setState(s)
        })
        .catch(() => {
          if (!cancelled) setState(null) // backend dev mode off — hide quietly
        })
    }

    // In Vite's dev server the backend almost always runs without a
    // SITE_PASSWORD gate (local contributors don't set it), so fire
    // immediately to preserve local-dev UX. In prod-style builds
    // (VITE_SHOW_DEV_TOOLS=1, e.g. staging) wait until the gate has
    // populated localStorage to avoid the spurious 401 in gh#247.
    if (import.meta.env.DEV || localStorage.getItem(SITE_PASSWORD_KEY)) {
      fetchProvider()
      return () => {
        cancelled = true
      }
    }

    // Gate not yet cleared — listen for the in-tab unlock signal that
    // SiteGate / TestingPasswordPanel dispatch on successful password
    // submission. Native `storage` events only fire cross-tab, so the
    // custom event is the same-tab equivalent.
    const onUnlock = () => fetchProvider()
    window.addEventListener('sitegate:unlocked', onUnlock)
    return () => {
      cancelled = true
      window.removeEventListener('sitegate:unlocked', onUnlock)
    }
  }, [devEnabled])

  if (!devEnabled || !state) return null

  const onChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const provider = e.target.value
    setPending(true)
    setError(null)
    try {
      const next = await apiPost<ProviderState>('/dev/provider', { provider })
      setState(next)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed')
    } finally {
      setPending(false)
    }
  }

  // Strip any "org/" prefix on model names so the visible label fits;
  // the full string stays available via the title attribute on hover.
  // Examples:
  //   "LGAI-EXAONE/K-EXAONE-236B-A23B" → "K-EXAONE-236B-A23B"
  //   "Qwen/Qwen3-235B-A22B-Instruct-2507" → "Qwen3-235B-A22B-Instruct-2507"
  //   "claude-sonnet-4-6" → "claude-sonnet-4-6"
  const shortenModel = (model: string): string => {
    const slashIdx = model.lastIndexOf('/')
    return slashIdx >= 0 ? model.slice(slashIdx + 1) : model
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 12,
        right: 12,
        zIndex: 9999,
        background: '#222',
        color: '#eee',
        padding: '8px 10px',
        borderRadius: 6,
        font: '12px/1.3 system-ui, sans-serif',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        opacity: pending ? 0.6 : 1,
        // Keep the widget compact even with the longer model strings;
        // truncation is fine because the full string is in `title`.
        maxWidth: 280,
      }}
      title="Dev-only: swap the backend LLM provider"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ opacity: 0.7 }}>LLM:</span>
        <select
          value={state.current}
          onChange={onChange}
          disabled={pending}
          style={{
            background: '#111',
            color: '#eee',
            border: '1px solid #444',
            borderRadius: 4,
            padding: '2px 4px',
            flex: 1,
            minWidth: 0,
          }}
        >
          {state.available.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {/* Model display. For OpenRouter, render free-text inputs so a
          developer can paste any ``vendor/model`` slug without editing
          .env + restarting (the OpenRouter catalog is huge). For other
          providers the env-var defaults are the right surface — no
          inputs, just the resolved-model labels. */}
      {state.current === 'openrouter' ? (
        <OpenRouterModelInputs state={state} setState={setState} disabled={pending} />
      ) : (
        (state.large_model || state.small_model) && (
          <div
            style={{
              marginTop: 6,
              fontSize: 11,
              lineHeight: 1.45,
              opacity: 0.85,
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              columnGap: 6,
            }}
          >
            {state.large_model && (
              <>
                <span
                  style={{ opacity: 0.7 }}
                  title="Used for: knowledge map + assessment setup"
                >
                  L:
                </span>
                <span
                  title={state.large_model}
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {shortenModel(state.large_model)}
                </span>
              </>
            )}
            {state.small_model && (
              <>
                <span
                  style={{ opacity: 0.7 }}
                  title="Used for: assessment, tutoring, summary"
                >
                  S:
                </span>
                <span
                  title={state.small_model}
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {shortenModel(state.small_model)}
                </span>
              </>
            )}
          </div>
        )
      )}

      {error && (
        <div style={{ marginTop: 4, color: '#f88', fontSize: 11 }}>{error}</div>
      )}
    </div>
  )
}

/**
 * Free-text inputs for the OpenRouter tier slugs. Renders inside the
 * provider-switcher widget when the active provider is openrouter.
 *
 * Save behavior: on click, POST to /dev/openrouter-models. Backend
 * validation rejects empty / no-vendor-prefix slugs with a 400 — the
 * error renders inline so the user knows what to fix. Successful
 * save updates the parent ``state`` so the dropdown's siblings see
 * the new tier slugs immediately.
 *
 * No persistence layer beyond the backend: the inputs initialize from
 * ``state.large_model`` / ``state.small_model`` (which the backend
 * tracks via process-level overrides). If the user types something
 * and reloads without saving, the input resets to the last-saved
 * value — which is what they'd want anyway (typing without saving
 * wasn't a deliberate change).
 */
function OpenRouterModelInputs({
  state,
  setState,
  disabled,
}: {
  state: ProviderState
  setState: (s: ProviderState) => void
  disabled: boolean
}) {
  const [large, setLarge] = useState(state.large_model || '')
  const [small, setSmall] = useState(state.small_model || '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Reset the inputs when the parent state changes (e.g. after another
  // tab saved different slugs and we just refetched). Without this,
  // the inputs would stick on whatever was typed locally even after
  // a successful refresh.
  //
  // Sync-input-to-external-state is the pattern react-hooks/set-state-
  // in-effect 7.1.1 flags, but is exactly the rule's docstring's
  // example of a legitimate effect role ("subscribe for updates from
  // some external system"). A pure derived-state refactor would lose
  // local-edit semantics — the inputs would stop being editable
  // controlled fields and snap back to the parent's value on every
  // render.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- (the rule only flags the first setState; setSmall below is covered)
    setLarge(state.large_model || '')
    setSmall(state.small_model || '')
  }, [state.large_model, state.small_model])

  const dirty =
    large !== (state.large_model || '') || small !== (state.small_model || '')

  const onSave = async () => {
    setSaving(true)
    setErr(null)
    try {
      const next = await apiPost<ProviderState>('/dev/openrouter-models', {
        large,
        small,
      })
      setState(next)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed')
    } finally {
      setSaving(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    background: '#111',
    color: '#eee',
    border: '1px solid #444',
    borderRadius: 4,
    padding: '2px 4px',
    fontFamily: 'inherit',
    fontSize: 11,
    width: '100%',
    boxSizing: 'border-box',
  }
  const labelStyle: React.CSSProperties = {
    opacity: 0.7,
    fontSize: 11,
    marginRight: 4,
  }

  return (
    <div
      style={{
        marginTop: 6,
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        rowGap: 4,
        columnGap: 6,
        alignItems: 'center',
      }}
    >
      <span style={labelStyle} title="Used for: knowledge map + assessment setup">
        L:
      </span>
      <input
        value={large}
        onChange={(e) => setLarge(e.target.value)}
        placeholder="vendor/model"
        disabled={disabled || saving}
        style={inputStyle}
        spellCheck={false}
      />
      <span style={labelStyle} title="Used for: assessment, tutoring, summary">
        S:
      </span>
      <input
        value={small}
        onChange={(e) => setSmall(e.target.value)}
        placeholder="vendor/model"
        disabled={disabled || saving}
        style={inputStyle}
        spellCheck={false}
      />
      <span />
      <button
        onClick={onSave}
        disabled={disabled || saving || !dirty}
        style={{
          background: dirty ? '#3b3b8a' : '#222',
          color: '#eee',
          border: '1px solid #555',
          borderRadius: 4,
          padding: '2px 6px',
          fontSize: 11,
          cursor: dirty && !saving ? 'pointer' : 'default',
          opacity: dirty ? 1 : 0.6,
        }}
        title={
          dirty
            ? 'Save tier slugs (process-level; resets on backend restart)'
            : 'No unsaved changes'
        }
      >
        {saving ? 'saving…' : 'save'}
      </button>
      {err && (
        <>
          <span />
          <span style={{ color: '#f88', fontSize: 11 }}>{err}</span>
        </>
      )}
    </div>
  )
}
